import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { addDays, startOfDay } from 'date-fns';
import { Info, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { FeatureDetailsTemplate, FeatureMode, FeatureTab, FeatureTabRef } from '@/modules/common';
import { StatusBadgeType } from '@/modules/common/components/molecules/badge/status-badge-types';
import { ScheduledChangeAlert } from '@/modules/common/components/molecules/scheduled-change-alert';
import { usePermissions } from '@/modules/common/hooks/use-permissions';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { SettingsRoutes } from '@/router/routes';
import {
  formatDisplayDate,
  getTodayISOString,
  normalizeToStartOfDayUTC,
} from '@/utils/date-formatters';

import { TimeOffSetupTab } from '../../components/time-offs/setup-tab/time-off-setup-tab';
import { TimeOffApplyChangesModal } from '../../components/time-offs/time-off-apply-changes-modal';
import { TimeOffChangesTab } from '../../components/time-offs/time-off-changes-tab';
import { TIME_OFF_CHANGES_FILTER_KEY_DATE_RANGE } from '../../constants/time-off-changes-filter-keys';
import { useTimeOffTypeChanges } from '../../hooks/time-offs/use-time-off-type-changes';
import { useTimeOffTypeDetails } from '../../hooks/time-offs/use-time-off-type-details';
import { useUpdateTimeOffType } from '../../hooks/time-offs/use-update-time-off-type';
import { TimeOffTypeCode } from '../../types/time-off';
import { TimeOffTypeChangeLogEntry } from '../../types/time-off-changes';
import { TimeOffTypeSetupFormData } from '../../validations/time-off-type-validation';

const SETUP_TAB = 'setup';
const CHANGES_TAB = 'changes';

export const TimeOffTypeDetailsPage = () => {
  const { typeId } = useParams<{ typeId: string }>();
  const id = Number(typeId);
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { t } = useTranslation('authenticated.settings');
  const { hasPermission } = usePermissions();

  const canEdit = hasPermission(OpsiumPolicy.TimeOffTypesEdit);

  const { data: entity, isLoading, error, refetch } = useTimeOffTypeDetails(id);

  const [currentTab, setCurrentTab] = useState(SETUP_TAB);
  const formStateChangeHandlerRef = useRef<
    ((isDirty: boolean, hasChanges?: boolean, isValid?: boolean) => void) | null
  >(null);
  const [pendingData, setPendingData] = useState<TimeOffTypeSetupFormData | null>(null);
  const [pendingEffectiveStartDate, setPendingEffectiveStartDate] = useState<Date | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  const mode = canEdit ? FeatureMode.Edit : FeatureMode.Read;

  const updateMutation = useUpdateTimeOffType({
    id,
    onSuccess: () => {
      setApplyOpen(false);
      setPendingData(null);
      setPendingEffectiveStartDate(null);
      setFormResetKey(key => key + 1);
    },
  });

  const { data: changesData } = useTimeOffTypeChanges({
    typeId: id,
    filters: {},
    enabled: Number.isFinite(id),
  });

  const nextScheduledChange = useMemo((): TimeOffTypeChangeLogEntry | undefined => {
    if (!changesData?.items.length) return undefined;

    const todayStr = getTodayISOString().slice(0, 10);

    return changesData.items
      .filter(change => change.startDate && change.startDate.slice(0, 10) > todayStr)
      .sort((first, second) => first.startDate.localeCompare(second.startDate))[0];
  }, [changesData]);

  const handleBack = useCallback(() => {
    if (location.key !== 'default') navigate(-1);
    else navigate(SettingsRoutes.TimeOff);
  }, [location.key, navigate]);

  const handleFormStateChangeHandlerReady = useCallback(
    (handler: (isDirty: boolean, hasChanges?: boolean, isValid?: boolean) => void) => {
      formStateChangeHandlerRef.current = handler;
    },
    []
  );

  const handleTabChange = useCallback(
    (tab: string, additionalParams?: Record<string, string>) => {
      setCurrentTab(tab);

      if (!additionalParams) return;

      setSearchParams(searchParams => {
        const nextSearchParams = new URLSearchParams(searchParams);
        Object.entries(additionalParams).forEach(([key, value]) => {
          nextSearchParams.set(key, value);
        });

        return nextSearchParams;
      });
    },
    [setSearchParams]
  );

  const guardedTabChangeHandlerRef = useRef<
    ((tab: string, additionalParams?: Record<string, string>) => void) | null
  >(null);

  const handleTabChangeHandlerReady = useCallback(
    (handler: (tab: string, additionalParams?: Record<string, string>) => void) => {
      guardedTabChangeHandlerRef.current = handler;
    },
    []
  );

  const setupRef = useRef<FeatureTabRef<TimeOffTypeSetupFormData>>(null);
  const changesRef = useRef<FeatureTabRef<TimeOffTypeSetupFormData>>(null);

  const handleShowScheduledChange = useCallback(
    (change: TimeOffTypeChangeLogEntry) => {
      const formattedDate = formatDisplayDate(change.startDate);
      const dateRangeParam = `${formattedDate}:${formattedDate}`;
      const tabChangeHandler = guardedTabChangeHandlerRef.current ?? handleTabChange;

      tabChangeHandler(CHANGES_TAB, {
        [TIME_OFF_CHANGES_FILTER_KEY_DATE_RANGE]: dateRangeParam,
      });
    },
    [handleTabChange]
  );

  const wrapSetupTabWithAlert = useCallback(
    (content: ReactNode) => {
      if (!nextScheduledChange) return content;

      return (
        <div className="space-y-4">
          <ScheduledChangeAlert
            date={formatDisplayDate(nextScheduledChange.startDate)}
            onShowChange={() => handleShowScheduledChange(nextScheduledChange)}
          />
          {content}
        </div>
      );
    },
    [handleShowScheduledChange, nextScheduledChange]
  );

  const tabs: FeatureTab<TimeOffTypeSetupFormData>[] = useMemo(
    () => [
      {
        id: SETUP_TAB,
        label: t('pages.timeOff.details.tabs.setup'),
        icon: Info,
        ref: setupRef,
        content: wrapSetupTabWithAlert(
          <TimeOffSetupTab
            ref={setupRef}
            mode={mode}
            initialData={entity}
            resetKey={formResetKey}
            onFormStateChange={(isDirty, hasChanges, isValid) =>
              formStateChangeHandlerRef.current?.(isDirty, hasChanges, isValid)
            }
          />
        ),
        maxWidth: 'max-w-2xl',
      },
      {
        id: CHANGES_TAB,
        label: t('pages.timeOff.details.tabs.changes'),
        icon: RefreshCw,
        ref: changesRef,
        content: <TimeOffChangesTab typeId={id} />,
        maxWidth: 'max-w-4xl',
      },
    ],

    [mode, entity, formResetKey, wrapSetupTabWithAlert, t, id]
  );

  const handleSave = useCallback(
    ({ data }: { data: TimeOffTypeSetupFormData & { effectiveStartDate?: Date } }) => {
      const effectiveStartDate = data.effectiveStartDate ?? null;
      const formData: TimeOffTypeSetupFormData = {
        allocationFormat: data.allocationFormat,
        allocationDays: data.allocationDays,
        dateOfRenewal: data.dateOfRenewal,
        allowCarryover: data.allowCarryover,
        carryoverDays: data.allowCarryover ? data.carryoverDays : null,
        allowNegativeBalance: data.allowNegativeBalance,
        negativeBalanceDays: data.allowNegativeBalance ? data.negativeBalanceDays : null,
        hasMaxLimit: data.hasMaxLimit,
        maxLimitDays: data.hasMaxLimit ? data.maxLimitDays : null,
        isApprovalRequired: data.isApprovalRequired,
        requireFileUpload: data.requireFileUpload,
        allowHalfDay: data.allowHalfDay,
        allowQuarterDay: data.allowQuarterDay,
        allowHourlyRequests: data.allowHourlyRequests,
        considerMemberCapacity: data.considerMemberCapacity,
        creditOnCreation: data.creditOnCreation,
        prorateAccrual: data.prorateAccrual,
      };
      setPendingData(formData);
      setPendingEffectiveStartDate(effectiveStartDate);
      setApplyOpen(true);
    },
    []
  );

  const handleConfirmApply = useCallback(async () => {
    if (!pendingData || !pendingEffectiveStartDate) return;
    await updateMutation.mutateAsync({
      data: pendingData,
      effectiveStartDate: normalizeToStartOfDayUTC(pendingEffectiveStartDate).toISOString(),
      isBillable: entity?.isBillable ?? false,
    });
  }, [entity?.isBillable, pendingData, pendingEffectiveStartDate, updateMutation]);

  // Redirect Public Holiday to list
  if (entity?.code === TimeOffTypeCode.PUBLIC_HOLIDAY) {
    navigate(SettingsRoutes.TimeOff, { replace: true });
    return null;
  }

  const entityForTemplate = entity ? { id: entity.id, name: entity.displayName } : undefined;

  return (
    <>
      <FeatureDetailsTemplate
        entityData={entityForTemplate}
        mode={mode}
        currentTab={currentTab}
        isLoading={updateMutation.isPending}
        isLoadingEntity={isLoading}
        error={error ?? undefined}
        tabs={tabs}
        onBack={handleBack}
        onFormStateChange={() => {}}
        onFormStateChangeHandlerReady={handleFormStateChangeHandlerReady}
        onTabChange={handleTabChange}
        onTabChangeHandlerReady={handleTabChangeHandlerReady}
        onRefetch={() => void refetch()}
        enableUnsavedChangesConfirmation
        pageTitle={entity?.displayName}
        showAvatar={false}
        showStatus={false}
        statusBadgeType={StatusBadgeType.Project}
        handleSave={handleSave as any}
        getDraftData={() => undefined}
        saveDraftData={() => {}}
        clearDraft={() => {}}
        getFinalSubmissionData={() =>
          setupRef.current?.getFormData() ?? ({} as TimeOffTypeSetupFormData)
        }
        hasDraftData={false}
        useGenericFooter
        footerProps={{
          showDatePicker: true,
          useSingleDatePicker: true,
          // Changes can only be scheduled from tomorrow onward — today and past
          // dates are not selectable.
          minDate: startOfDay(addDays(new Date(), 1)),
          labels: {
            saveChanges: t('pages.timeOff.details.footer.save'),
            saving: t('common:loading'),
            dateOfChange: t('pages.timeOff.details.footer.dateOfChange'),
            selectChangeDate: t('pages.timeOff.details.footer.selectChangeDate'),
          },
        }}
        patchResultToDisplay={null}
        modals={
          <TimeOffApplyChangesModal
            open={applyOpen}
            onCancel={() => setApplyOpen(false)}
            onConfirm={handleConfirmApply}
            isLoading={updateMutation.isPending}
          />
        }
      />
    </>
  );
};
